import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import Sidebar from "../components/Sidebar";
import ChapterRenderer from "../components/ChapterRenderer";
import { AcademyWaitlistLock } from "../components/AcademyWaitlistLock";
import { CHAPTERS, getChapter } from "../data/chapters";
import { useProgress } from "../context/ProgressContext";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { IMAGES, CHAPTER_HEROES } from "../data/assets";
import { Menu, Check, Printer, ChevronLeft, ChevronRight, CheckCircle2, Upload, LayoutDashboard, ChevronDown, Lock } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UPLOAD_LOCK_MSG = "You've used your free coach review. Enroll in the Technical Track ($29/mo) for unlimited video reviews.";
const SWIPE_HINT_KEY = "nexto_swipe_hint_seen";
const W = () => (typeof window !== "undefined" ? window.innerWidth : 400);
const FLIP = {
  enter: (d) => ({ x: d > 0 ? W() : d < 0 ? -W() : 0, opacity: d ? 0.4 : 0, rotateY: d > 0 ? -14 : d < 0 ? 14 : 0 }),
  center: { x: 0, opacity: 1, rotateY: 0, transition: { type: "spring", stiffness: 260, damping: 30 } },
  exit: (d) => ({ x: d > 0 ? -W() * 0.7 : d < 0 ? W() * 0.7 : 0, opacity: 0, rotateY: d > 0 ? 12 : d < 0 ? -12 : 0, transition: { duration: 0.28, ease: "easeIn" } }),
};

// Mobile chapter-dot strip: where you are in the Blueprint, tap to jump.
const ChapterDots = ({ idx, completed, onJump }) => (
  <div data-testid="chapter-dots" className="md:hidden flex items-center justify-center gap-1.5 px-4 py-2 border-b border-white/5 bg-[#0d0d0d]/70 no-print">
    {CHAPTERS.map((c, i) => {
      const active = i === idx, done = completed.includes(c.id);
      return (
        <button key={c.id} data-testid={`chapter-dot-${c.id}`} onClick={() => onJump(c.id)} aria-label={c.title} aria-current={active ? "page" : undefined}
          className="h-6 flex items-center px-0.5">
          <span className="block rounded-full transition-all duration-300" style={{ width: active ? 18 : 6, height: 6, background: active ? "#D4AF37" : done ? "#C41E24" : "rgba(255,255,255,0.22)", boxShadow: active ? "0 0 10px rgba(212,175,55,0.8)" : "none" }} />
        </button>
      );
    })}
  </div>
);

export default function Reader() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { completed, toggleChapter } = useProgress();
  const { hasAccess, token, player, refresh } = usePlayerAuth();
  const fileRef = useRef();
  const dirRef = useRef(0);
  const x = useMotionValue(0);
  const rotateY = useTransform(x, [-420, 0, 420], [10, 0, -10]);
  const flipHint = useTransform(x, [-140, -60, 60, 140], [1, 0, 0, 1]);
  const [uploading, setUploading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isTouch] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const chapter = getChapter(chapterId);
  const idx = CHAPTERS.findIndex((c) => c.id === chapterId);
  const numLabel = String(idx + 1).padStart(2, "0");
  const uploadLocked = player?.can_upload === false;

  useEffect(() => { window.scrollTo(0, 0); const el = document.getElementById("reader-main"); if (el) el.scrollTo(0, 0); }, [chapterId]);
  useEffect(() => { if (chapterId) localStorage.setItem("nexto_last_chapter", chapterId); }, [chapterId]);
  useEffect(() => {
    if (!isTouch || localStorage.getItem(SWIPE_HINT_KEY)) return;
    setShowHint(true);
    const t = setTimeout(() => { setShowHint(false); localStorage.setItem(SWIPE_HINT_KEY, "1"); }, 4000);
    return () => clearTimeout(t);
  }, [isTouch]);

  if (!chapter) return <Navigate to="/" replace />;
  const done = completed.includes(chapter.id);
  const prev = CHAPTERS[idx - 1];
  const next = CHAPTERS[idx + 1];
  const flipTo = (target, d) => { if (!target) return; dirRef.current = d; localStorage.setItem(SWIPE_HINT_KEY, "1"); setShowHint(false); navigate(`/read/${target.id}`); };

  const handleComplete = () => {
    toggleChapter(chapter.id);
    if (!done) {
      track("chapter_complete", { chapter_id: chapter.id });
      toast.success(`Chapter ${numLabel} complete!`, { description: "Keep training. Keep moving forward." });
    }
  };

  const onUploadProof = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 60 * 1024 * 1024) { toast.error("Video too large (max 60MB)."); if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("move_label", `${chapter.title} proof`);
    try {
      await axios.post(`${API}/proofs`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      toast.success("Video proof uploaded! Coach Barker will review it.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDragEnd = (_, info) => {
    const w = window.innerWidth, dx = info.offset.x, vx = info.velocity.x;
    if ((dx < -w / 3 || (dx < -60 && vx < -600)) && next) flipTo(next, 1);
    else if ((dx > w / 3 || (dx > 60 && vx > 600)) && prev) flipTo(prev, -1);
  };

  return (
    <div className="flex h-screen bg-[#111111] text-white overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="w-72 flex-shrink-0 border-r border-white/10 hidden md:block no-print">
        <Sidebar />
      </aside>

      {/* Main */}
      <main id="reader-main" className="flex-1 overflow-y-auto overflow-x-hidden scroll-smooth relative print-full with-bottom-nav" style={{ perspective: 1200 }}>
        {/* Sticky header */}
        <header className="sticky top-0 z-30 bg-[#111111]/85 backdrop-blur-xl border-b border-white/10 px-4 sm:px-8 pb-3 header-safe-3 flex items-center justify-between gap-3 no-print">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="md:hidden p-2 rounded-sm hover:bg-white/5" aria-label="Open menu"><Menu size={20} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-[#141414] border-white/10">
                <Sidebar />
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#C41E24] font-bold">Chapter {numLabel}</div>
              <div className="font-headings text-base uppercase tracking-wide truncate">{chapter.title}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasAccess && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="back-to-hub-btn"
                    disabled={uploading}
                    className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white bg-white/10 hover:bg-white/20 border border-white/15 hover:border-white/40 px-3 py-2 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24] disabled:opacity-60"
                    aria-label="Player Hub menu"
                  >
                    <LayoutDashboard size={14} /> <span className="hidden sm:inline">{uploading ? "Uploading…" : "Player Hub"}</span> <ChevronDown size={12} className="opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-[#1A1A1A] border-white/10 text-white">
                  <DropdownMenuItem
                    data-testid="quick-upload-clip-btn"
                    onClick={() => uploadLocked ? toast.info(UPLOAD_LOCK_MSG) : fileRef.current?.click()}
                    className="cursor-pointer focus:bg-[#C41E24] focus:text-white gap-2 font-semibold"
                  >
                    {uploadLocked ? <Lock size={15} className="text-white/50" /> : <Upload size={15} className="text-[#C41E24]" />} {uploadLocked ? "Upload Locked" : "Upload a Clip"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="open-hub-menu-btn"
                    onClick={() => navigate("/hub")}
                    className="cursor-pointer focus:bg-white/10 gap-2 font-semibold"
                  >
                    <LayoutDashboard size={15} className="text-white/70" /> Open Player Hub
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <button data-testid="print-btn" onClick={() => window.print()} className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 rounded-sm transition-colors" aria-label="Export PDF">
              <Printer size={14} /> PDF
            </button>
            <button
              data-testid="mark-complete-btn"
              onClick={handleComplete}
              className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24] ${done ? "bg-white/10 text-white" : "bg-[#C41E24] hover:bg-[#A0161D] text-white"}`}
            >
              {done ? <><CheckCircle2 size={14} /> <span className="hidden sm:inline">Completed</span></> : <><Check size={14} /> <span className="hidden sm:inline">Mark Complete</span></>}
            </button>
          </div>
        </header>
        <ChapterDots idx={idx} completed={completed} onJump={(id) => flipTo(CHAPTERS.find((c) => c.id === id), CHAPTERS.findIndex((c) => c.id === id) > idx ? 1 : -1)} />

        <AnimatePresence mode="wait" custom={dirRef.current}>
          <motion.div
            key={chapter.id}
            data-testid="chapter-page"
            custom={dirRef.current}
            variants={FLIP}
            initial="enter"
            animate="center"
            exit="exit"
            drag={isTouch ? "x" : false}
            dragDirectionLock
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.32}
            onDragEnd={onDragEnd}
            style={{ x, rotateY, transformOrigin: "50% 50%", transformStyle: "preserve-3d" }}
            className="relative will-change-transform"
          >
        {/* Chapter hero */}
        <div className="relative h-56 sm:h-72 overflow-hidden print-page">
          <img loading="lazy" decoding="async" src={CHAPTER_HEROES[chapter.id] || IMAGES.chapterBg} alt="" className="w-full h-full object-cover" draggable={false} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/70 to-[#111111]/40" />
          <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-12 max-w-4xl mx-auto w-full left-0 right-0">
            <div className="font-cover text-7xl sm:text-8xl text-[#C41E24]/25 leading-none">{numLabel}</div>
            <h1 className="font-headings text-4xl sm:text-5xl uppercase tracking-wide leading-none -mt-4">{chapter.title}</h1>
            <p className="font-subheadings text-sm sm:text-base font-semibold text-white/70 mt-2">{chapter.kicker}</p>
          </div>
          <AnimatePresence>
            {showHint && (
              <motion.div data-testid="swipe-hint" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="md:hidden absolute top-4 inset-x-0 flex justify-center pointer-events-none">
                <span className="inline-flex items-center gap-2 bg-black/70 backdrop-blur border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
                  <motion.span animate={{ x: [-3, 3, -3] }} transition={{ repeat: Infinity, duration: 1.2 }}><ChevronLeft size={12} /></motion.span>
                  Swipe to turn the page
                  <motion.span animate={{ x: [3, -3, 3] }} transition={{ repeat: Infinity, duration: 1.2 }}><ChevronRight size={12} /></motion.span>
                </span>
              </motion.div>
            )}
          </AnimatePresence>
          {isTouch && (
            <motion.div style={{ opacity: flipHint }} className="absolute inset-y-0 inset-x-0 pointer-events-none flex items-center justify-between px-3">
              <span className="w-9 h-9 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.8)]"><ChevronLeft size={18} /></span>
              <span className="w-9 h-9 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.8)]"><ChevronRight size={18} /></span>
            </motion.div>
          )}
        </div>

        {/* Content */}
          <article className="max-w-4xl mx-auto px-6 sm:px-12 py-10">
            <ChapterRenderer chapter={chapter} />

            {/* Paid-player chapter actions: prominent upload + mark complete */}
            {hasAccess && (
              <div data-testid="chapter-actions" className="mt-14 rounded-xl border border-[#C41E24]/40 bg-gradient-to-r from-[#C41E24]/12 to-[#141414] p-6 sm:p-8 no-print">
                <h3 className="font-headings text-2xl uppercase tracking-wide">Show Your Work</h3>
                <p className="text-sm text-white/55 mt-1 mb-6">{uploadLocked ? "You've used your free coach review. Mark chapters done to keep tracking your progress." : "Film a short clip of a move from this chapter, then mark it done to log your progress."}</p>
                <input ref={fileRef} data-testid="chapter-proof-file" type="file" accept="video/*" className="hidden" onChange={onUploadProof} />
                <div className="flex flex-col sm:flex-row gap-4">
                  {uploadLocked ? (
                    <div data-testid="chapter-upload-locked" className="flex-1">
                      <AcademyWaitlistLock note="You've used your free coach review. Enroll in the Technical Track for unlimited reviews with personal voice feedback." />
                    </div>
                  ) : (
                    <button
                      data-testid="chapter-upload-proof-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="flex-1 flex items-center justify-center gap-3 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider text-base sm:text-lg px-6 py-5 rounded-lg shadow-[0_10px_30px_rgba(196,30,36,0.35)] transition-colors disabled:opacity-60"
                    >
                      <Upload size={22} /> {uploading ? "Uploading…" : "Upload Video Proof"}
                    </button>
                  )}
                  <button
                    data-testid="chapter-mark-complete-btn"
                    onClick={handleComplete}
                    className={`flex-1 flex items-center justify-center gap-3 font-subheadings font-bold uppercase tracking-wider text-base sm:text-lg px-6 py-5 rounded-lg transition-colors ${done ? "bg-white/10 text-white border border-white/20" : "bg-white text-[#111] hover:bg-white/90"}`}
                  >
                    {done ? <><CheckCircle2 size={22} /> Chapter Completed</> : <><Check size={22} /> Mark Chapter as Complete</>}
                  </button>
                </div>
              </div>
            )}

            {/* Chapter footer nav */}
            <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between gap-4 no-print">
              {prev ? (
                <button data-testid="prev-chapter-btn" onClick={() => flipTo(prev, -1)} className="group flex items-center gap-2 text-left max-w-[45%]">
                  <ChevronLeft size={20} className="text-[#C41E24] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Previous</div>
                    <div className="text-sm font-semibold text-white/80 group-hover:text-white truncate">{prev.title}</div>
                  </div>
                </button>
              ) : <span />}
              {next ? (
                <button data-testid="next-chapter-btn" onClick={() => flipTo(next, 1)} className="group flex items-center gap-2 text-right max-w-[45%] ml-auto">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Next</div>
                    <div className="text-sm font-semibold text-white/80 group-hover:text-white truncate">{next.title}</div>
                  </div>
                  <ChevronRight size={20} className="text-[#C41E24] flex-shrink-0" />
                </button>
              ) : (
                <button data-testid="finish-btn" onClick={() => navigate("/hub")} className="ml-auto flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-sm transition-colors">
                  Back to Hub <ChevronRight size={16} />
                </button>
              )}
            </div>
          </article>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
